// What happens after "Create group": the booking is saved, the WhatsApp group is
// created, the invitations go out, and only then does the advisor land on the
// desk with the new group already in the list. These functions read a group row
// to say which of those it is at, and wait until the last one is done. No React
// here, so the rules can be tested.

export const POLL_INTERVAL_MS = 2000;
export const POLL_TIMEOUT_MS = 150000;

const text = (v) => String(v ?? "").trim();

// Where a booking's group is, from its row (as /api/groups returns it).
//   creating_group  - saved, but the WhatsApp group does not exist yet
//   sending_invites - the group exists, the invitations are still going out
//   ready           - the invitations have all been handed over
//   error           - the group could not be created
// `warning` is set on a ready row whose invitations partly failed, so the
// advisor is told rather than left to find out.
export function groupProgress(row) {
  if (!row) return { phase: "creating_group" };
  const status = text(row["Status"]);
  const lastError = text(row["Last Error"]);
  if (status === "Error") return { phase: "error", error: lastError || "The group could not be created." };
  if (status !== "Created" || !text(row["Group ID"])) return { phase: "creating_group" };
  if (!text(row["Invites Done At"])) return { phase: "sending_invites" };
  return /invite/i.test(lastError) ? { phase: "ready", warning: lastError } : { phase: "ready" };
}

// Polls the groups list until this booking's row is ready, has failed, or the
// time runs out. `fetchGroups` returns the list payload ({ rows }) and is
// expected to also put it in the app's cache, which is what makes the new group
// already be in the desk list when the advisor gets there.
export async function waitForGroupReady({
  fetchGroups,
  rowId,
  onPhase = () => {},
  isCancelled = () => false,
  sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  now = () => Date.now(),
  intervalMs = POLL_INTERVAL_MS,
  timeoutMs = POLL_TIMEOUT_MS,
}) {
  const started = now();
  let lastPhase = null;
  let row = null;
  for (;;) {
    if (isCancelled()) return { outcome: "cancelled", row };
    try {
      const data = await fetchGroups();
      row = (data?.rows || []).find((r) => r._row === rowId) || null;
    } catch {
      // A failed read is not a failed booking: keep waiting and try again.
    }
    const progress = groupProgress(row);
    if (progress.phase !== lastPhase) {
      lastPhase = progress.phase;
      onPhase(progress.phase);
    }
    if (progress.phase === "ready") return { outcome: "ready", row, warning: progress.warning || "" };
    if (progress.phase === "error") return { outcome: "error", row, error: progress.error };
    if (now() - started >= timeoutMs) return { outcome: "timeout", row, phase: progress.phase };
    await sleep(intervalMs);
  }
}

// The checklist the advisor watches. `phase` is where the booking is now:
// uploading | saving | creating_group | sending_invites | opening_desk.
// `hasUploads` adds the first row for forms that upload at submit.
export function progressSteps(phase, { hasUploads = false, failed = false } = {}) {
  const order = [
    ...(hasUploads ? [{ key: "uploading", label: "Uploading your files" }] : []),
    { key: "saving", label: "Saving the booking" },
    { key: "creating_group", label: "Creating the WhatsApp group" },
    { key: "sending_invites", label: "Sending invitations" },
    { key: "opening_desk", label: "Adding it to your desk" },
  ];
  const at = order.findIndex((s) => s.key === phase);
  return order.map((s, i) => {
    let state = "pending";
    if (at === -1) state = phase === "done" ? "done" : "pending";
    else if (i < at) state = "done";
    else if (i === at) state = failed ? "error" : "active";
    return { ...s, state };
  });
}
